import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, AlertTriangleIcon, BookOpenIcon, StarIcon, UserPlusIcon } from 'lucide-react';
import { AfterTideLayout } from '../../components/AfterTide/AfterTideLayout';
import { AfterTideBookCard } from '../../components/AfterTide/AfterTideBookCard';
import { aftertideAuthors, aftertideBooks, aftertideSeries, aftertideReviews } from '../../data/aftertideBooksData';
// Helper to get genre badge color
const getGenreColor = (genre: string): string => {
  const g = genre.toLowerCase();
  if (g.includes('romance')) return 'bg-pink-900/40 text-pink-200 border-pink-700/50';
  if (g.includes('fantasy')) return 'bg-indigo-900/40 text-indigo-200 border-indigo-700/50';
  if (g.includes('sci-fi') || g.includes('science')) return 'bg-blue-900/40 text-blue-200 border-blue-700/50';
  if (g.includes('mystery')) return 'bg-indigo-900/40 text-indigo-200 border-indigo-700/50';
  if (g.includes('thriller')) return 'bg-red-900/40 text-red-200 border-red-700/50';
  if (g.includes('horror')) return 'bg-gray-800/60 text-gray-200 border-gray-600/50';
  if (g.includes('historical')) return 'bg-amber-900/40 text-amber-200 border-amber-700/50';
  if (g.includes('young adult') || g.includes('ya')) return 'bg-cyan-900/40 text-cyan-200 border-cyan-700/50';
  if (g.includes('contemporary')) return 'bg-emerald-900/40 text-emerald-200 border-emerald-700/50';
  if (g.includes('erotica')) return 'bg-rose-900/40 text-rose-200 border-rose-700/50';
  if (g.includes('cyberpunk')) return 'bg-blue-900/40 text-blue-200 border-blue-700/50';
  if (g.includes('gothic')) return 'bg-gray-800/60 text-gray-200 border-gray-600/50';
  return 'bg-violet-900/40 text-violet-200 border-violet-700/50';
};
export default function AfterTideAuthorProfile() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const author = aftertideAuthors.find(a => a.slug === slug);
  if (!author) {
    return <AfterTideLayout data-id="element-4860" data-mp="3kkxl8x7qmvk0">
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-id="element-4861" data-mp="3kkftacs3z3hw">
          <AlertTriangleIcon className="h-12 w-12 text-[#e2b04a] mb-4" data-id="element-4862" data-mp="3pd8x8yfr67sx" />
          <h1 className="font-playfair text-3xl font-bold text-[#f5f0e8] mb-2" data-id="element-4863" data-mp="3uaqwfx3yx8nq">
            Author Not Found
          </h1>
          <p className="text-[#a0a0b0] mb-6" data-id="element-4864" data-mp="2xn45gepnyiho">
            The author you're looking for doesn't exist or has been removed.
          </p>
          <button onClick={() => navigate('/aftertide/browse')} className="px-6 py-2 bg-[#e2b04a] text-[#1a1a2e] font-bold rounded-lg hover:bg-[#c79a3f] transition-colors" data-id="element-4865" data-mp="36m7q3n4hb5ya">
            Browse Catalog
          </button>
        </div>
      </AfterTideLayout>;
  }
  // Get author's books
  const authorBooks = aftertideBooks.filter(b => {
    const bSlug = b.authorSlug || b.author.toLowerCase().replace(/\s+/g, '-');
    return bSlug === author.slug;
  });
  // Get author's series
  const authorSeriesList = aftertideSeries.filter(s => s.authorSlug === author.slug);
  // Get reviews for author's books
  const bookIds = authorBooks.map(b => b.id);
  const authorReviews = aftertideReviews.filter(r => bookIds.includes(r.bookId)).slice(0, 6);
  return <AfterTideLayout data-id="element-4866" data-mp="1smd0creckmii">
      <div className="bg-[#1a1a2e] min-h-screen pb-20" data-id="element-4867" data-mp="1sno1ejgerg5x">
        {/* Top Navigation */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-4868" data-mp="1nn09lvkcuvan">
          <button onClick={() => navigate(-1)} className="inline-flex items-center text-[#a0a0b0] hover:text-[#e2b04a] transition-colors text-sm font-medium" data-id="element-4869" data-mp="1iogofxdl5njc">
            <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-4870" data-mp="1drqabflcd20k" />
            Back
          </button>
        </div>

        {/* Author Hero */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16" data-id="element-4871" data-mp="382v6jyu3mc5v">
          <div className="flex flex-col md:flex-row gap-8 items-start" data-id="element-4872" data-mp="3g42wbvmyyumc">
            {/* Avatar */}
            <div className="flex-shrink-0" data-id="element-4873" data-mp="3l35q2jxi1ef0">
              <img src={author.avatar} alt={author.name} className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-[#16213e] shadow-[0_0_20px_rgba(226,176,74,0.15)]" data-id="element-4874" data-mp="3l24bw6l6sbt2" />
            </div>

            {/* Info */}
            <div className="flex-grow" data-id="element-4875" data-mp="2xcjtppwuxiku">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4" data-id="element-4876" data-mp="2xbi7qqeazboc">
                <div data-id="element-4877" data-mp="326e8cexlv7d2">
                  <h1 className="font-playfair text-3xl md:text-4xl font-bold text-[#f5f0e8] mb-2" data-id="element-4878" data-mp="2nfnivdjhkkd4">
                    {author.name}
                  </h1>
                  <div className="flex items-center gap-3 flex-wrap" data-id="element-4879" data-mp="2sersg5abok3z">
                    <span className="px-3 py-1 bg-[#16213e] border border-[#e2b04a]/30 text-[#e2b04a] rounded-sm text-xs font-bold uppercase tracking-wider" data-id="element-4880" data-mp="2efs30vezk16r">
                      {author.tierRange}
                    </span>
                    {author.genres.map((genre, i) => <span key={i} className={`px-2 py-0.5 rounded-sm text-xs border ${getGenreColor(genre)}`} data-id="element-4881" data-mp="1utgqol8ti9s3" data-mpi={i}>
                        {genre}
                      </span>)}
                  </div>
                </div>
                <button className="flex items-center justify-center px-6 py-2 bg-transparent border border-[#e2b04a] text-[#e2b04a] font-medium rounded-lg hover:bg-[#e2b04a] hover:text-[#1a1a2e] transition-colors" data-id="element-4882" data-mp="2iq32f2ns9mjb">
                  <UserPlusIcon className="h-4 w-4 mr-2" data-id="element-4883" data-mp="2rhwlavqsb9c2" />
                  Follow
                </button>
              </div>
              <p className="text-[#a0a0b0] leading-relaxed max-w-3xl" data-id="element-4884" data-mp="2njpcqo6h5eby">
                {author.bio}
              </p>
            </div>
          </div>
        </div>

        {/* Series Section */}
        {authorSeriesList.length > 0 && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16" data-id="element-4885" data-mp="1u5ejz75ilrf2">
            <h2 className="font-playfair text-2xl font-bold text-[#f5f0e8] mb-6 border-b border-[#16213e] pb-2" data-id="element-4886" data-mp="1u31wkegzmoum">
              Series by {author.name}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4887" data-mp="233zx2ms7we8g">
              {authorSeriesList.map(series => <Link key={series.id} to={`/aftertide/series/${series.id}`} className="bg-[#16213e] border border-[#1a1a2e] rounded-xl p-6 hover:border-[#e2b04a]/50 transition-colors group" data-id="element-4888" data-mp="27y48bi8in8fc">
                  <div className="flex justify-between items-start mb-3" data-id="element-4889" data-mp="2lxc92fkbuoln">
                    <h3 className="font-playfair text-xl font-bold text-[#f5f0e8] group-hover:text-[#e2b04a] transition-colors" data-id="element-4890" data-mp="2qvdsx2pkury6">
                      {series.name}
                    </h3>
                    <span className="px-2 py-1 bg-[#1a1a2e] text-[#a0a0b0] rounded-sm text-xs font-medium" data-id="element-4891" data-mp="wgky35wiq57a">
                      {series.bookIds.length} Books
                    </span>
                  </div>
                  <p className="text-[#a0a0b0] text-sm mb-4 line-clamp-2" data-id="element-4892" data-mp="k6ljbzfl63nj">
                    {series.description}
                  </p>
                  <span className="text-xs font-bold text-[#e2b04a] uppercase tracking-wider" data-id="element-4893" data-mp="f81wzxx2ypun">
                    {series.tierRange}
                  </span>
                </Link>)}
            </div>
          </div>}

        {/* Books Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16" data-id="element-4894" data-mp="12yedvyaazwxd">
          <h2 className="font-playfair text-2xl font-bold text-[#f5f0e8] mb-6 border-b border-[#16213e] pb-2" data-id="element-4895" data-mp="xy052e0voiya">
            Books by {author.name}
          </h2>
          {authorBooks.length > 0 ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6" data-id="element-4896" data-mp="3io92m9z22x62">
              {authorBooks.map(book => <div key={book.id} className="h-[280px] sm:h-[320px]" data-id="element-4897" data-mp="3elm3rp3ywbzd">
                  <AfterTideBookCard book={book} data-id="element-4898" data-mp="1hl84uofs0ddb" />
                </div>)}
            </div> : <p className="text-[#a0a0b0] italic" data-id="element-4899" data-mp="1mmxgo6atov2g">No books available yet.</p>}
        </div>

        {/* Reader Reviews Section */}
        {authorReviews.length > 0 && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4900" data-mp="1dlpjjrk1m09y">
            <h2 className="font-playfair text-2xl font-bold text-[#f5f0e8] mb-6 border-b border-[#16213e] pb-2" data-id="element-4901" data-mp="21aimw92zvael">
              Reader Reviews
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4902" data-mp="25b2ekx4stltc">
              {authorReviews.map(review => {
            const book = authorBooks.find(b => b.id === review.bookId);
            return <div key={review.id} className="bg-[#16213e] border border-[#1a1a2e] rounded-xl p-6" data-id="element-4903" data-mp="2k9ecf73wr0m3">
                    <div className="flex justify-between items-start mb-3" data-id="element-4904" data-mp="2p15xx387ig5d">
                      <div data-id="element-4905" data-mp="2ydbxcsswjcuj">
                        <p className="font-medium text-[#f5f0e8]" data-id="element-4906" data-mp="2yck1dgve7d3q">
                          {review.reviewer}
                        </p>
                        <p className="text-xs text-[#a0a0b0]" data-id="element-4907" data-mp="384zjce6ow1om">
                          {new Date(review.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                        </p>
                      </div>
                      <div className="flex" data-id="element-4908" data-mp="2bg9ziibi4z00">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-4 w-4 ${i < review.rating ? 'text-[#e2b04a]' : 'text-gray-600'}`} fill={i < review.rating ? 'currentColor' : 'none'} data-id="element-4909" data-mp="2pblwyxh1l3km" data-mpi={i} />)}
                      </div>
                    </div>
                    {book && <p className="text-xs text-[#e2b04a] mb-3 font-medium" data-id="element-4910" data-mp="21g99kbsuxrwv">
                        Reviewed: {book.title}
                      </p>}
                    <p className="text-sm text-[#a0a0b0] italic mb-4" data-id="element-4911" data-mp="1rn0ul58els71">
                      "{review.content}"
                    </p>
                    {review.tierContext && <span className="inline-block px-2 py-1 bg-[#1a1a2e] text-[#a0a0b0] rounded-sm text-xs" data-id="element-4912" data-mp="1ioyo4a5wi7cd">
                        {review.tierContext}
                      </span>}
                  </div>;
          })}
            </div>
          </div>}
      </div>
    </AfterTideLayout>;
}